'use client';

import { usePathname } from 'next/navigation';

import { Box, Drawer, styled, useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';

import { useReactiveVar } from '@apollo/client/react';

import { scenarioEditorDrawerOpenVar } from '@/common/cache';
import { useTranslation } from '@/common/i18n';
import { useInstance } from '@/common/instance';
import { formatUrl } from '@/common/links';
import ScenarioEditor from '@/components/scenario/ScenarioEditor';
import { useSiteOrNull } from '@/context/site';
import IntroModal from './common/IntroModal';
import { FooterSlot, GlobalNavSlot } from './custom';
import { RefreshPrompt } from './general/RefreshPrompt';

const DRAWER_WIDTH = 320;

const StyledSkipToContent = styled('a')`
  position: absolute;
  left: -9999px;
  z-index: 999;
  padding: ${({ theme }) => theme.spaces.s050};
  background-color: ${({ theme }) => theme.brandDark};
  border: ${({ theme }) => `${theme.btnBorderWidth} solid ${theme.themeColors.light}`};
  border-radius: ${({ theme }) => theme.btnBorderRadius};
  color: ${({ theme }) => theme.themeColors.light};
  opacity: 0;

  &:focus,
  &:visited {
    color: ${({ theme }) => theme.themeColors.light};
  }

  &:focus {
    left: 50%;
    top: ${({ theme }) => theme.spaces.s050};
    transform: translateX(-50%);
    opacity: 1;
  }
`;

const visuallyHiddenSx = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap' as const,
  border: 0,
};

type MenuPage = {
  id?: string | null;
  title: string;
  menuLabel?: string | null;
  urlPath: string;
};

function isMenuPage(
  page: unknown
): page is { id?: unknown; title?: unknown; menuLabel?: unknown; urlPath?: unknown } {
  if (typeof page !== 'object' || page === null) return false;
  const p = page as Record<string, unknown>;
  return (
    ('id' in p ? typeof p.id === 'string' || p.id === null || typeof p.id === 'undefined' : true) &&
    typeof p.title === 'string' &&
    typeof p.urlPath === 'string'
  );
}

const Layout = ({ children }: React.PropsWithChildren) => {
  const pathname = usePathname();
  const theme = useTheme();
  const site = useSiteOrNull();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { menuPages, additionalLinkPages } = site ?? {};
  const drawerOpen = useReactiveVar(scenarioEditorDrawerOpenVar);

  const handleDrawerClose = () => {
    scenarioEditorDrawerOpenVar(false);
  };

  let activePage: MenuPage | undefined;

  const rawMenu = Array.isArray(menuPages) ? menuPages : [];
  const menuItems: MenuPage[] = rawMenu.filter(isMenuPage).map((page) => ({
    id: typeof page.id === 'string' ? page.id : null,
    title: page.title,
    menuLabel: typeof page.menuLabel === 'string' ? page.menuLabel : null,
    urlPath: page.urlPath,
  }));

  const rawAdditionalLinkPages = Array.isArray(additionalLinkPages) ? additionalLinkPages : [];
  const additionalLinkItems: MenuPage[] = rawAdditionalLinkPages.filter(isMenuPage).map((page) => ({
    id: typeof page.id === 'string' ? page.id : null,
    title: page.title,
    urlPath: page.urlPath,
  }));

  // `urlPath` is the raw backend path (e.g. `/some-page`), while `pathname`
  // includes the instance basePath and locale prefix. Resolve each menu page to
  // its full path before comparing.
  const resolvedMenuItems = menuItems.map((page) => ({
    page,
    fullPath: formatUrl(site, page.urlPath),
  }));

  resolvedMenuItems.forEach(({ page, fullPath }) => {
    if (pathname === fullPath) {
      activePage = page;
    }
  });

  if (!activePage) {
    resolvedMenuItems.forEach(({ page, fullPath }) => {
      if (pathname.startsWith(fullPath)) {
        activePage = page;
      }
    });
  }

  const navItems = menuItems.map((page) => ({
    id: page.id || '',
    name: page.menuLabel || page.title,
    slug: page.urlPath,
    urlPath: page.urlPath,
    active: page === activePage,
  }));

  const instance = useInstance();

  const title = instance.introContent?.find(
    (
      block
    ): block is { __typename: 'RichTextBlock'; id: string | null; field: string; value: string } =>
      block.__typename === 'RichTextBlock' && block.field === 'title'
  )?.value;

  const paragraph = instance.introContent?.find(
    (
      block
    ): block is { __typename: 'RichTextBlock'; id: string | null; field: string; value: string } =>
      block.__typename === 'RichTextBlock' && block.field === 'paragraph'
  )?.value;

  const introModalEnabled = !!(title && paragraph);
  const showRefreshPrompt = instance.features.showRefreshPrompt;

  return (
    <>
      <StyledSkipToContent href="#main">{t('skip-to-main-content')}</StyledSkipToContent>

      <Box sx={{ display: 'flex', maxWidth: '100%', minWidth: 0 }}>
        {/* Persistent drawer for desktop */}
        <Drawer
          sx={{
            display: { xs: 'none', md: 'block' },
            width: drawerOpen ? DRAWER_WIDTH : 0,
            flexShrink: 0,
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
          variant="persistent"
          anchor="left"
          open={!isMobile && drawerOpen}
          slotProps={{
            paper: {
              sx: {
                width: DRAWER_WIDTH,
                boxSizing: 'border-box',
                backgroundColor: theme.graphColors.blue010,
                borderRadius: 0,
                boxShadow: 10,
              },
            },
          }}
        >
          {drawerOpen && !isMobile && <ScenarioEditor handleDrawerClose={handleDrawerClose} />}
        </Drawer>
        {/* Temporary drawer for mobile */}
        <Drawer
          variant="temporary"
          open={isMobile && drawerOpen}
          onClose={handleDrawerClose}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              backgroundColor: theme.graphColors.blue010,
              borderRadius: 0,
              boxShadow: 10,
            },
          }}
          slotProps={{
            root: {
              keepMounted: true,
            },
          }}
        >
          {drawerOpen && isMobile && <ScenarioEditor handleDrawerClose={handleDrawerClose} />}
        </Drawer>
        {showRefreshPrompt && <RefreshPrompt />}
        <Box
          sx={{
            flexGrow: 1,
            flexShrink: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100vh',
          }}
        >
          <GlobalNavSlot
            siteTitle={site?.title || ''}
            ownerName={site?.owner || undefined}
            navItems={navItems}
          />
          <Box component="main" className="main" id="main" sx={{ flexGrow: 1 }}>
            {/* hidden H1 fallback for accessibility */}
            <Box component="h1" sx={visuallyHiddenSx}>
              {activePage?.title || site?.title || ''}
            </Box>
            {children}
          </Box>
          <FooterSlot additionalLinks={additionalLinkItems} />
        </Box>
      </Box>
      {introModalEnabled && <IntroModal title={title} paragraph={paragraph} />}
    </>
  );
};

export default Layout;
